/* eslint-disable react/no-children-prop */
import { useMutation } from "@apollo/client"
import CloseIcon from "@mui/icons-material/Close"
import { FileIcon, defaultStyles } from "react-file-icon"
import { Box, Button, Divider, List, ListItem, TextField, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"
import React, { useContext, useState } from "react"
import { CenterCircular } from "../Loading"
import { useJobContext } from "../../contexts/JobContext"
import SiteContext from "../../contexts/SiteContext"
import { UserContext } from "../../contexts/UserContext"
import { CREATE_DOCUMENT_MUTATION } from "../../entities/job/graphql/mutations"
import {
    createFileFromBase64,
    getDescriptionByStatus,
    uploadFileToS3,
    getFileExtension,
} from "../../entities/job/helper"
import CustomCamera from "../Camera"
import Signature from "../Signature"
import MediaOptionsDrawer from "../MediaOptionsDrawer"
import { stageTypes } from "../../entities/job/interface"

const imagesStyle = {
    overflow: "auto",
    "&::-webkit-scrollbar": {
        display: "none",
        width: "0px",
    },
}
const FileIcons = ({ extension }: { extension: string }) => {
    const iconProps = defaultStyles[extension as keyof typeof defaultStyles]
    return (
        <>
            <FileIcon extension={extension} {...iconProps} />
        </>
    )
}
const Proof = ({
    stage,
    handleJobCompletion,
    handleBackNav,
}: {
    stage: string
    handleJobCompletion: any
    handleBackNav: (show: boolean) => void
}) => {
    const { t } = useTranslation()
    const { currentJobs } = useJobContext()
    const [checkDriver, setCheckDriver] = useState(false)
    const [showCamera, setShowCamera] = useState(false)
    const [showSignature, setShowSignature] = useState(false)
    const [consigneeInput, setConsigneeInput] = useState<string | null>(null)
    const [images, setImages] = useState<string[]>([])
    const [signatureImg, setSignatureImg] = useState<string>()
    const [documentUploading, setDocumentUploading] = useState<boolean>(false)

    const siteContext = useContext(SiteContext)
    const userContext = useContext(UserContext)
    const theme = useTheme()
    const [createDocument] = useMutation(CREATE_DOCUMENT_MUTATION)

    // Handle context missing
    if (!userContext && siteContext) {
        throw new Error("userContext is not provided")
    }
    if (!currentJobs) return

    const siteId = siteContext?.siteId

    const driverName = userContext ? userContext?.user?.name : ""

    const handleDeleteImage = (index: number) => {
        setImages((prevImages: string[]) => {
            const newImages = prevImages.filter((_, i) => i !== index)
            return newImages
        })
    }
    const cameraOpen = () => setShowCamera(true)
    const renderCameraButton = () => (
        <Box width={"100%"} display="flex" flexDirection="column" alignItems={"center"}>
            <MediaOptionsDrawer cameraOpen={cameraOpen} setImages={setImages} />
        </Box>
    )

    const renderImages = () => (
        <Box width="100%" maxWidth={700} display="flex" justifyContent="center" alignItems="center" marginY={1}>
            <List
                dense
                sx={imagesStyle}
                style={{ display: "inline-flex", padding: "10px", flexWrap: "nowrap", paddingLeft: "15px" }}
            >
                {images?.map((image, index) => {
                    const isImage = image.startsWith("data:image")
                    const fileType = image.split(";")[0].split("/")[1]
                    const extension = getFileExtension(fileType)

                    return (
                        <ListItem key={index} style={{ width: 120, display: "flex", justifyContent: "center" }}>
                            {isImage ? (
                                <img src={image} alt="Captured" style={{ width: 100, height: 100, borderRadius: 15 }} />
                            ) : (
                                <FileIcons extension={extension} />
                            )}
                            <CloseIcon
                                onClick={() => handleDeleteImage(index)}
                                style={{
                                    position: "relative",
                                    top: "-50%",
                                    right: "15px",
                                    color: "red",
                                    background: "white",
                                    borderRadius: "50%",
                                }}
                            />
                        </ListItem>
                    )
                })}
            </List>
        </Box>
    )

    //handle submit
    const handleUpload = async (imageDataArray: { image: string; consigneeName?: string }[], status: string) => {
        setDocumentUploading(true)
        const localFilePaths = imageDataArray
        for (let index = 0; index < localFilePaths.length; index++) {
            const imageData = localFilePaths[index]

            // Upload signatures at index 0
            if (index === 0) {
                const signature =
                    status === "pickup"
                        ? siteContext?.site?.documentTypes?.POP_Signature
                        : siteContext?.site?.documentTypes?.POD_Signature

                if (signature) {
                    currentJobs?.map(async (job) => {
                        return await uploadDocument(
                            imageData.image,
                            status,
                            signature,
                            imageData.consigneeName,
                            job._id
                        )
                    })

                    continue
                }
            }

            const image =
                status === "pickup" ? siteContext?.site?.documentTypes?.POP : siteContext?.site?.documentTypes?.POD

            if (image) {
                currentJobs?.map(async (job) => {
                    await uploadDocument(imageData.image, status, image, imageData.consigneeName, job._id)
                })
            }
            await handleJobCompletion(currentJobs)
            setDocumentUploading(false)
        }
    }

    const uploadDocument = async (
        image: string,
        status: string,
        docTypeId: string,
        consigneeName?: string,
        jobId?: string
    ) => {
        // Extract the MIME type dynamically from the base64 string
        const mimeTypeMatch = image?.match(/^data:([^;]+);base64,/)
        const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : null

        if (!mimeType) {
            throw new Error("Invalid file format.")
        }

        const file = createFileFromBase64(image, mimeType)
        let description = getDescriptionByStatus(status)
        if (consigneeName) {
            description = `${description}. Consignee: ${consigneeName}`
        }
        const record = {
            description,
            owner: { id: siteId, type: "SITE" },
            fileName: file.name,
            type: docTypeId ?? undefined,
            about: { id: jobId, type: "JOB" },
        }

        try {
            const { data } = await createDocument({
                variables: {
                    record: {
                        ...record,
                        contentType: file.type,
                    },
                },
            })
            const uploadUrl = data.createDocument.uploadUrl
            await uploadFileToS3(uploadUrl, file)
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <>
            {showCamera ? (
                <CustomCamera handleClosed={() => setShowCamera(false)} images={images} setImages={setImages} />
            ) : showSignature ? (
                <Signature
                    setImg={(signatureImg: string) => {
                        setSignatureImg(signatureImg)
                    }}
                    handleClose={() => {
                        setShowSignature(false)
                        handleBackNav(true)
                    }}
                />
            ) : (
                <Box
                    width={"90%"}
                    maxWidth={800}
                    display="flex"
                    flexDirection="column"
                    maxHeight={"80vh"}
                    bgcolor={"primary.main"}
                    position={"absolute"}
                    top={"6vh"}
                    borderRadius={6}
                    paddingY={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    overflow={"auto"}
                >
                    {documentUploading && <CenterCircular />}

                    <Box width={"100%"} display="flex" flexDirection="column" alignItems={"center"} position="relative">
                        <Typography variant="h5" paddingTop={2} color="white">
                            {stage === stageTypes.PICKUP ? "Proof of Pickup" : "Proof of Delivery"}
                        </Typography>
                        <Divider
                            sx={{
                                borderColor: "white",
                                opacity: 0.5,
                                borderWidth: 1,
                                borderStyle: "dashed",
                                width: "90%",
                                marginBottom: 2,
                                marginTop: 2,
                                alignSelf: "center",
                                "@media (min-height: 1100px)": {
                                    marginBottom: -2,
                                },
                            }}
                        />
                    </Box>
                    <Box width={"100%"} display="flex" flexDirection="column" alignItems={"center"} paddingY={2}>
                        <Box
                            width={"100%"}
                            textAlign={"center"}
                            paddingX={1}
                            marginY={{ xs: 1.5, sm: 2, md: 2.5 }}
                            sx={{
                                "@media (max-height: 600px)": {
                                    marginY: 1,
                                },
                            }}
                        >
                            <Typography variant="body2" color="white" sx={{ fontWeight: "Medium", fontSize: "1.1rem" }}>
                                {stage === stageTypes.PICKUP
                                    ? currentJobs[0]?.originAddress?.address
                                    : currentJobs[0]?.destinationAddress?.address}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {currentJobs[0]?.jobDate?.toString()}
                            </Typography>
                        </Box>
                        <Box
                            width="100%"
                            textAlign="center"
                            marginY={{ xs: 2, sm: 2.5, md: 3 }}
                            sx={{
                                "@media (max-height: 600px)": {
                                    marginY: 1,
                                },
                            }}
                        >
                            {currentJobs?.flatMap(
                                (job, jobIndex) =>
                                    job.items?.map((item, itemIndex) => {
                                        const quantity =
                                            stage === stageTypes.PICKUP ? item.quantityLoaded : item.quantityUnloaded
                                        return (
                                            <Typography key={`${jobIndex}-${itemIndex}`} variant="body2" color="white">
                                                {`${quantity} x ${item.unit} x ${item.name}`}
                                            </Typography>
                                        )
                                    }) || []
                            )}
                        </Box>

                        <Divider
                            sx={{
                                borderColor: "white",
                                opacity: 0.5,
                                borderWidth: 1,
                                borderStyle: "dashed",
                                width: "90%",
                                "@media (min-height: 1100px)": {
                                    marginBottom: -2,
                                    marginTop: -2,
                                },
                            }}
                        />
                    </Box>

                    <Box width={"100%"} display="flex" flexDirection="column" alignItems={"center"}>
                        <Box
                            width={"90%"}
                            textAlign={"center"}
                            p={1}
                            m={1}
                            sx={{
                                "@media (max-height: 600px)": {
                                    marginY: 1,
                                },
                            }}
                        >
                            {/* First Box: Buttons */}
                            <Box display="flex" width="100%">
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    onClick={() => setCheckDriver(false)}
                                    style={{ flex: 1, marginRight: "4px" }} // 50% width
                                >
                                    {t("jobs.mobile.buttons.consignee")}
                                </Button>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    onClick={() => setCheckDriver(true)}
                                    style={{ flex: 1, marginLeft: "4px" }} // 50% width
                                >
                                    {t("jobs.mobile.buttons.driver")}
                                </Button>
                            </Box>

                            {/* Second Box: Conditional Content */}
                            <Box display="flex" width="100%" pt={1}>
                                <TextField
                                    id="consignee"
                                    placeholder="Consignee"
                                    variant="outlined"
                                    fullWidth
                                    onChange={(e) => setConsigneeInput(e.target.value)}
                                    value={checkDriver ? driverName : consigneeInput} // Set value based on condition
                                    disabled={checkDriver}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#004367", // Set background color
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {images.length > 0 ? renderImages() : null}
                    {images.length < 5 && renderCameraButton()}

                    {/* 5 is the maximum number of images */}
                    <Box
                        width={"100%"}
                        display={"flex"}
                        justifyContent={"center"}
                        flexDirection={"column"}
                        alignItems={"center"}
                        marginY={1}
                        marginBottom={1}
                    >
                        {signatureImg ? (
                            <Box width={"90%"} maxWidth={500}>
                                <img
                                    src={signatureImg}
                                    alt="Captured"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: 15,
                                        backgroundColor: theme.palette.common.white,
                                    }}
                                />
                            </Box>
                        ) : null}
                        {signatureImg ? (
                            <Box
                                width={"90%"}
                                textAlign={"center"}
                                p={1}
                                m={1}
                                sx={{
                                    "@media (max-height: 600px)": {
                                        marginY: 1,
                                    },
                                }}
                            >
                                <Box display="flex" width="100%">
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            setShowSignature(true)
                                            handleBackNav(false)
                                        }}
                                        style={{ flex: 1, marginRight: "4px" }} // 50% width
                                    >
                                        {t("jobs.mobile.buttons.resign")}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        style={{ flex: 1, marginLeft: "4px" }} // 50% width
                                        onClick={() => {
                                            const signature = { image: signatureImg, consigneeName: consigneeInput }
                                            const imagesToUpload = images.map((image) => ({ image }))
                                            handleUpload([signature, ...imagesToUpload], stage)
                                        }}
                                    >
                                        {t("jobs.mobile.buttons.submit")}
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => {
                                    setShowSignature(true)
                                    handleBackNav(false)
                                }}
                                style={{ flex: 1, marginRight: "4px" }} // 50% width
                            >
                                {t("jobs.mobile.buttons.sign")}
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
        </>
    )
}

export default Proof
